import React, { PropsWithChildren, PureComponent } from 'react';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import classNames from 'classnames';

import CustomSelect from 'components/ui/customSelect';
import InputAmount from 'components/ui/inputAmount';
import SelectItemType from 'types/SelectItem';
import { AnyObject } from 'types/Common';
import Input from '../../../../components/ui/input/Input';
import { BaseFields, BaseFilledFields } from './types';

import './singleRequest.scss';
import PayoutStepper from './components/PayoutStepper';
import { addPermissions, WithPermissions } from '../../../../decorators/addPermissions';
import Messages from '../../../../constants/rpcTypes';
import GeneralInfoPreview from './components/GeneralInfoPreview';
import { SinglePayoutContext, STEP_1, STEP_2 } from './context';

interface Props extends IntlProps, WithPermissions {
  baseFields: BaseFields;
  dictionaries: {
    currencies: SelectItemType[];
    projects: SelectItemType[];
    methods: SelectItemType[];
  };
  onChange: (field: any, value: any) => void;
  validationErrors: AnyObject;
  isProjectFetch: boolean;
  isClarification: boolean;
  isPreFilled: boolean;
}

class SingleRequest extends PureComponent<PropsWithChildren<Props>> {
  render() {
    const {
      onChange,
      baseFields,
      dictionaries,
      isProjectFetch,
      validationErrors,
      isPreFilled,
      isClarification,
      getTranslate,
      isEnabled
    } = this.props;

    const { activeStep } = this.context;

    const isPayoutFormWithStep2Enabled = isEnabled(Messages.PaymentOperation_PayoutFormWithStep2);

    const { currencies, projects, methods } = dictionaries;
    const {
      amount,
      currency,
      projectId,
      paymentMethodUrlAlias,
      extraParam,
    }: BaseFields | BaseFilledFields = baseFields;

    return (
      <div className='ui-form-fields__container ui-form-fields__content'>
        {isPayoutFormWithStep2Enabled && <PayoutStepper />}
        <div className="ui-form-fields__text ui-form-fields__container_header">
          {activeStep === STEP_1
            ? getTranslate('payouts.request.single.infoText')
            : getTranslate('payouts.request.single.infoTextBack')
          }
        </div>
        <div className='ui-form-fields__inner'>
          <div className='ui-form-fields__col ui-form-fields__col_general'>
            <div className='ui-form-fields__col-title'>
              {getTranslate('payouts.request.single.generalInfo.label')}
            </div>
            <>
              {activeStep === STEP_2 && <GeneralInfoPreview baseFields={{
                amount,
                currency,
                projectId,
                paymentMethodUrlAlias,
                extraParam,
              } as BaseFilledFields} />}
              {activeStep === STEP_1 && <CustomSelect
                id="general.projectId"
                label={getTranslate('payouts.request.single.projectLabel')}
                placeholder={getTranslate(
                  'payouts.request.single.projectPlaceholder'
                )}
                value={projectId}
                customClass='ui-form-fields__item utils-form-item'
                onChange={(item) => onChange('projectId', item)}
                options={projects}
                isSearchable
                isDisabled={isPreFilled || isClarification}
                containerSelector='.ui-form-fields__content'
                modern
              />}
              {activeStep === STEP_1 && this.isMethods() ? (
                <>
                  <div className='ui-form-fields__item'>
                    <CustomSelect
                      id='method'
                      isDisabled={
                        (!isProjectFetch && !projectId) ||
                        this.isPreFilledCard() ||
                        isClarification
                      }
                      isLoading={isProjectFetch}
                      label={getTranslate('payouts.request.single.methodLabel')}
                      value={paymentMethodUrlAlias}
                      placeholder={getTranslate(
                        'payouts.request.single.methodPlaceholder'
                      )}
                      customClass='ui-form-fields__field ui-select_payment-method'
                      onChange={(item) =>
                        onChange('paymentMethodUrlAlias', item)
                      }
                      options={methods}
                      containerSelector='.ui-form-fields__content'
                      modern
                    />
                  </div>
                  <div className='ui-form-fields__item utils-flex'>
                    <div className='ui-form-fields__item'>
                      <InputAmount
                        id='payment.amount'
                        label={getTranslate(
                          'payouts.request.single.amountLabel'
                        )}
                        placeholder={getTranslate(
                          'payouts.request.single.amountPlaceholder'
                        )}
                        value={amount}
                        error={
                          validationErrors?.['payment.amount'] ||
                          validationErrors?.['amount']
                        }
                        customClass='ui-form-fields__field'
                        onChange={(event) =>
                          onChange('amount', event.target.value)
                        }
                        disabled={isClarification}
                        modern
                      />
                    </div>

                    <div className='ui-form-fields__item'>
                      <CustomSelect
                        id='payment.currency'
                        label={getTranslate('payouts.request.single.currency')}
                        placeholder={getTranslate(
                          'payouts.request.single.currency.placeholder'
                        )}
                        value={this.checkSelectOption(currency)}
                        customClass='ui-form-fields__field utils-select-menu-width'
                        onChange={(value) => onChange('currency', value?.value)}
                        options={currencies}
                        isDisabled={
                          this.isPreFilledCard() ||
                          isClarification ||
                          !currencies
                        }
                        isSearchable
                        isLoading={!currencies}
                        containerSelector='.ui-form-fields__content'
                        tooltip={getTranslate(
                          'select.currency.favorite.tooltip'
                        )}
                        modern
                      />
                    </div>
                  </div>
                  <div className='ui-form-fields__item utils-flex'>
                    <div className='ui-form-fields__item'>
                      <Input
                        id='extraParam'
                        label={getTranslate(
                          'payouts.request.single.extraParamLabel'
                        )}
                        placeholder={getTranslate(
                          'payouts.request.single.extraParamPlaceholder'
                        )}
                        onChange={(event) =>
                          onChange('extraParam', event.target.value)
                        }
                        value={extraParam || ''}
                        error={validationErrors?.['extra_param']}
                        modern
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {activeStep === STEP_1 && <p>
                    {getTranslate('payouts.request.single.noAvailableProjects')}
                  </p>}
                </>
              )}
            </>
          </div>
          <div
            className={classNames(
              'ui-form-fields__col ui-form-fields__col_additional'
            )}>
            <div className='ui-form-fields__col-title'>
              {getTranslate('payouts.request.single.additionalInfo.label')}
            </div>
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }

  checkSelectOption = (param) => {
    const selectedCurrency = this.props.dictionaries.currencies.find(
      (currency) => currency.value === param
    );

    return selectedCurrency ? selectedCurrency : null;
  };

  isFirstStepFulfilled = () => {
    const { baseFields, isProjectFetch } = this.props;
    const { amount, currency, projectId } = baseFields;

    return !(
      currency &&
      parseFloat(amount) > 0 &&
      projectId &&
      !isProjectFetch
    );
  };

  isPreFilledCard = () => {
    const { baseFields, isPreFilled } = this.props;
    const { paymentMethodUrlAlias } = baseFields;
    return paymentMethodUrlAlias?.value === 'card' && isPreFilled;
  };

  isMethods = () => {
    const { dictionaries, baseFields, isProjectFetch } = this.props;
    const { projectId } = baseFields;
    const { methods } = dictionaries;

    return (
      !projectId ||
      !!(projectId && isProjectFetch) ||
      !!(projectId && methods.length)
    );
  };
}

SingleRequest.contextType = SinglePayoutContext;

export default addTranslation(addPermissions(SingleRequest));
